import { SelectionModel } from '@angular/cdk/collections';
import { NgFor, NgIf } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, zip } from 'rxjs';
import { BreadcrumbComponent } from 'src/app/shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { QMSPaginatorComponent } from 'src/app/shared/components/qms-paginator/qms-paginator.component';
import { LogType } from 'src/app/shared/enum/log-type.enum';
import { BreadcrumbItem } from 'src/app/shared/models/breadcrumb.interface';
import { LocationInformation } from 'src/app/shared/models/location.model';
import { LogModel } from 'src/app/shared/models/log.model';
import { DateFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import { LogService } from 'src/app/shared/services/log.service';
import { environment } from 'src/environments/environment';
import { RoleService } from '../../../services/role.service';
import { SearchEmployeeFilter } from '../../user/models/search-emplyee-filter';
import { AddMemberToRoleRequest, EmployeeModel } from '../../user/models/user-model';
import { RoleModel } from '../../user/user-add-edit/user-add-edit.component';
import { AddMemberToRoleComponent } from '../dialog/add-member-to-role/add-member-to-role.component';
import { ChemicalDbPermissionModel } from '../models/permission.model';
import { RoleMember, RoleMemberTypes } from '../models/role-member';
import { SearchRoleFilter } from '../models/search-role-filter.model';
import { SecurityGroup } from '../models/security-group.model';

export interface UserGroupInfo {
  name: string;
  id: number;
}

@Component({
  selector: 'app-role-add-edit',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    DateFormatPipe,
    QMSPaginatorComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatTabsModule,
    MatSortModule,
    MatDatepickerModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatCheckboxModule,
    NgIf,
    NgFor,
    TranslateModule,
    RouterModule,
  ],
  templateUrl: './role-add-edit.component.html',
  styleUrl: './role-add-edit.component.scss',
})
export class RoleAddEditComponent {
  displayedColumns: string[] = ['select', 'name', 'type', 'userName', 'email', 'organization'];
  dataSource = new MatTableDataSource<any>();
  selection = new SelectionModel<any>(true, []);
  totalCount = 0;
  addMemberToRoleDialog!: MatDialogRef<AddMemberToRoleComponent, any>;
  listEmployeeCanAdd: EmployeeModel[] = [];

  breadcrumbItems: BreadcrumbItem[] = [];
  generalForm!: UntypedFormGroup;
  disableEditName = false;
  isAddMode = false;
  filter: SearchRoleFilter = new SearchRoleFilter();
  permission: ChemicalDbPermissionModel = new ChemicalDbPermissionModel();
  permissionChanged: ChemicalDbPermissionModel = new ChemicalDbPermissionModel();
  accessManageLocations: LocationInformation[] = [];
  accessManageUserGroups: UserGroupInfo[] = [];
  isSystemAdmin = environment.server?.hasAdminRole;
  isAnonymousRole = false;

  roleMember: RoleMember[] = [];
  roleEntityOld: SecurityGroup = new SecurityGroup();

  constructor(
    public formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private roleService: RoleService,
    private logService: LogService,
    private toastr: ToastrService,
  ) {
    this.generalForm = formBuilder.group({
      id: [''],
      name: ['', [Validators.required]],
      description: [''],
      numberOfEmployees: [0],
      responsibleTypeId: [0],
      department: [''],
      isDeputy: [false],
      checkDuplicateName: [false],
    });
  }

  ngOnInit(): void {
    console.log(this.generalForm.value);
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isAddMode = false;
        this.generalForm.patchValue({ id: params['id'] });
        this.loadData();
      } else {
        this.isAddMode = true;
      }

      this.buildBreadcrumb();
    });
  }

  loadData() {
    combineLatest([
      this.roleService.getRoleMemberPaging(this.generalForm.value.id, this.filter),
      this.roleService.getSecurityGroups(true),
      this.roleService.getPermissionByRole(this.generalForm.value.id),
      this.roleService.getManageableLocationByUserGroupId(this.generalForm.value.id),
      this.roleService.getManageableUserGroupsByUserGroupId(this.generalForm.value.id),
    ]).subscribe(([dataRoleMember, allRoles, permissions, manageableLocations, manageableRoles]) => {
      this.roleMember = dataRoleMember.roleMember;
      this.totalCount = dataRoleMember.totalCount;
      this.generalForm.patchValue(allRoles.find((item) => item?.id == this.generalForm.value.id) || {});

      this.roleEntityOld = { ...this.generalForm.value };
      this.handleRoleMemberResponse();

      this.permission = permissions[0];

      this.accessManageLocations = manageableLocations ?? [];
      this.accessManageUserGroups = manageableRoles ?? [];

      this._checkDisable();
    });
  }

  loadDataForEditRoleMember(): void {
    combineLatest([
      this.roleService.getRoleMemberPaging(this.generalForm.value.id, this.filter),
      this.roleService.getSecurityGroups(true),
    ]).subscribe(([dataRoleMember, allRoles]) => {
      this.roleMember = dataRoleMember.roleMember;
      this.totalCount = dataRoleMember.totalCount;
      this.generalForm.patchValue(allRoles.find((item) => item?.id == this.generalForm.value.id) || {});

      this.roleEntityOld = { ...this.generalForm.value };
      this.handleRoleMemberResponse();

      this._checkDisable();
    });
  }

  loadDataForEditRolePermission(): void {
    this.roleService.getPermisionByRole(this.generalForm.value.id).subscribe((res) => {
      if (res) {
        this.permission = res[0];
      }
    });
  }

  handleRoleMemberResponse() {
    this.dataSource = new MatTableDataSource<any>(
      this.roleMember.map((item) => {
        const row = {
          id: item.id,
          name: item.name,
          type: this.getTypeName(item.memberType, item.department),
          userName: item.userName,
          email: item.email,
          organization: item.department,
        };
        return row;
      }),
    );
  }

  private _checkDisable(): void {
    const roleName = (this.generalForm.value.name ?? '').trim();
    this.disableEditName = ['Anonymous Role'].includes(roleName);
    this.isAnonymousRole = 'Anonymous Role' === roleName;
  }

  getTypeName(memberType: RoleMemberTypes, departmentName: string): void {
    return memberType == RoleMemberTypes.Person
      ? this.translate.instant('COMMON.MEMBER')
      : `${this.translate.instant('COMMON.LEADER')} - ${departmentName}`;
  }

  buildBreadcrumb() {
    this.breadcrumbItems = [
      {
        name: 'SIDE_BAR.HOME',
        url: '/',
      },
      {
        name: 'SIDE_BAR.MANAGE_ROLE',
        url: '/admin/roles',
      },
      {
        name: this.isAddMode ? 'ROLE.ADD_ROLE' : 'ROLE.EDIT_ROLE',
        url: '',
      },
    ];
  }

  searchByKeyword() {
    this.filter.pageIndex = 0;
    this.filter.pageIndex = 0;
    this.loadDataForEditRoleMember();
  }

  onSubmit() {
    this.generalForm.value.name = this.generalForm.value.name.trim();
    if (!this.generalForm.value.name) return;
    if (this.generalForm.value.id) {
      this.generalForm.value.checkDuplicateName = this.generalForm.value.name != this.roleEntityOld.name;
      this.permission.roleId = this.generalForm.value.id;
      zip(
        this.roleService.updatePermissionByRole(this.permission),
        this.roleService.updateRole(this.generalForm.value),
        this.roleService.updateAccessManageLocationsForUserGroup(
          this.generalForm.value.id,
          this.permission.userAccess_FullEdit ? this.accessManageLocations.map((l) => l.id) : [],
        ),
        this.roleService.updateAccessManageUserGroupsForUserGroup(
          this.generalForm.value.id,
          this.permission.userAccess_FullEdit ? this.accessManageUserGroups.map((r) => r.id) : [],
        ),
      ).subscribe(
        ([updatePermissionRes, updateInfoRes, updatedAccessManageLocation, updatedAccessManageUserGroups]) => {
          if (updateInfoRes) {
            this.toastr.success(this.translate.instant('ROLE.ROLE_UPDATED'));
            this.saveLogUpdateRole(this.generalForm.value.name);
            this.saveLogUpdatePermission();
            this.router.navigate(['admin/roles']);
          } else {
            this.toastr.success(this.translate.instant('ROLE.ROLE_NAME_IS_EXISTED'));
          }
        },
      );
    } else {
      this.generalForm.value.checkDuplicateName = true;
      this.roleService.createRole(this.generalForm.value).subscribe((res) => {
        if (Number.isInteger(res)) {
          this.toastr.success(this.translate.instant('ROLE.NEW_ROLE_CREATED'));
          this.generalForm.value.id = res;
          this.loadDataForEditRolePermission();
          this.saveLogCreateRole(this.generalForm.value.name);
          this.router.navigate(['admin/roles/' + res]);
        } else {
          this.toastr.success(this.translate.instant('ROLE.ROLE_NAME_IS_EXISTED'));
        }
      });
    }
  }

  onAddNewMember(): void {
    this.addMemberToRoleDialog = this.dialog.open(AddMemberToRoleComponent, {
      autoFocus: false,
      width: '1000px',
      height: '70vh',
      disableClose: true,
    });
    this.addMemberToRoleDialog.componentInstance.searchEvent.subscribe((filter) => {
      this.getEmployeeByMultiParams(filter);
    });

    this.getEmployeeByMultiParams(new SearchEmployeeFilter());


    this.addMemberToRoleDialog.componentInstance.onAddMemberEvent.subscribe((memberIds: number[]) => {
      const addMemberToRole: AddMemberToRoleRequest = new AddMemberToRoleRequest();
      addMemberToRole.roleId = this.generalForm.value.id;
      addMemberToRole.employeeIds = memberIds;
      addMemberToRole.employeeNames = this.listEmployeeCanAdd
        .filter((item) => memberIds.some((id) => id == item.id))
        .map((item) => item.fullName);
      this.roleService.addMemberToRole(addMemberToRole).subscribe((res) => {
        this.loadDataForEditRoleMember();
        this.addMemberToRoleDialog.close();
      });
    });
  }

  getEmployeeByMultiParams(filter: SearchEmployeeFilter): void {
    filter.excludedEmployeeId = this.dataSource.data.map((item) => item.id);
    this.roleService.searchEmployeesByMultipleParam(filter).subscribe((res) => {
      this.addMemberToRoleDialog.componentInstance.employees = res.employees;
      this.addMemberToRoleDialog.componentInstance.totalEmployee = res.total;
      this.listEmployeeCanAdd = res.employees;
    });
  }

  saveLogUpdateRole(name: string): void {
    if (
      this.generalForm.value.name != this.roleEntityOld.name ||
      this.roleEntityOld.description != this.generalForm.value.description
    ) {
      const log: LogModel = new LogModel();
      log.logType = LogType.updateRole;
      log.description = ` update role <strong>${name}</strong>`;
      log.descriptionNo = ` oppdater rolle <strong>${name}</strong>`;
      this.logService.saveLog(log).subscribe();
    }
  }

  saveLogCreateRole(name: string): void {
    const log: LogModel = new LogModel();
    log.logType = LogType.createRole;
    log.description = ` created role  <strong>${name}</strong>`;
    log.descriptionNo = ` opprettet rolle <strong>${name}</strong>`;
    this.logService.saveLog(log).subscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: RoleModel): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
  }

  bulkDeleteMemberInRole(): void {
    const dialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        actionText: this.translate.instant('COMMON.CONFIRMED'),
        content: this.translate.instant('ROLE.CONFIRM_DELETE_MEMBER_MESSAGE'),
      },
      width: '500px',
    });

    dialog.afterClosed().subscribe((confirm) => {
      if (confirm) {
        const membersDeleted = this.dataSource.data.filter((item) =>
          this.selection.selected.some((id) => id == item.id),
        );
        const deleteRoleMemberRequest = {
          roleId: this.generalForm.value.id,
          memberIds: this.selection.selected.map((x) => x.id),
          memberNames: [...membersDeleted.map((item) => item.name)],
        };
        this.roleService.deleteRoleMember(deleteRoleMemberRequest).subscribe((res) => {
          this.loadDataForEditRoleMember();
        });
      }
    });
  }

  onChangePermission(key: any, value: boolean): void {
    Object.keys(this.permission).forEach((property: any) => {
      if (property == key) {
        (this.permission as any)[`${property}`] = value;
        this.changePermissionLog(key, value);
      }
    });
  }

  changePermissionLog(key: any, value: any) {
    if (!(this.permissionChanged as any)[key]) {
      (this.permissionChanged as any)[key] = true;
      return;
    }
    if ((this.permissionChanged as any)[key]) {
      (this.permissionChanged as any)[key] = false;
    }
  }

  pageEvent(event: PageEvent) {
    this.filter.pageIndex = event.pageIndex;
    this.filter.pageSize = event.pageSize;
  }

  saveLogUpdatePermission(): void {
    if (Object.entries(this.permissionChanged).length > 0) {
      const permissionTxt: string[] = [];
      for (const [key, value] of Object.entries(this.permissionChanged)) {
        if (key == 'location_View') permissionTxt.push(this.translate.instant('ROLE.LOCATION.VIEW'));
        if (key == 'location_Add') permissionTxt.push(this.translate.instant('ROLE.LOCATION.ADD'));
        if (key == 'location_Edit') permissionTxt.push(this.translate.instant('ROLE.LOCATION.EDIT'));
        if (key == 'location_Delete') permissionTxt.push(this.translate.instant('ROLE.LOCATION.DELETE'));
        if (key == 'location_AddSubstance') permissionTxt.push(this.translate.instant('ROLE.LOCATION.ADD_SUBSTANCE'));
        if (key == 'sdsManager_View') permissionTxt.push(this.translate.instant('ROLE.SDS_MANAGER.VIEW'));
        if (key == 'sdsManager_Upload') permissionTxt.push(this.translate.instant('ROLE.SDS_MANAGER.UPLOAD'));
        if (key == 'sdsManager_Edit') permissionTxt.push(this.translate.instant('ROLE.SDS_MANAGER.EDIT'));
        if (key == 'sdsManager_Delete') permissionTxt.push(this.translate.instant('ROLE.SDS_MANAGER.DELETE'));
        if (key == 'sdsManager_ViewPortal') permissionTxt.push(this.translate.instant('ROLE.SDS_MANAGER.VIEW_PORTAL'));
        if (key == 'sdsManager_AddFromPortal')
          permissionTxt.push(this.translate.instant('ROLE.SDS_MANAGER.ADD_FROM_PORTAL'));
        if (key == 'riskAnalysis_Add') permissionTxt.push(this.translate.instant('ROLE.RISK_ANALYSIS.MANAGERMENT'));
        if (key == 'workAssignment_WorkAssignmentManager')
          permissionTxt.push(this.translate.instant('SIDE_BAR.WORK_ASSSIGNMENT'));
        if (key == 'workAssignment_ShowExecutiveInformation')
          permissionTxt.push(this.translate.instant('ROLE.WORK_ASSIGNMENT.SHOW_EXECUTIVE_INFO'));
        if (key == 'workAssignment_EditAndDuplicateWA')
          permissionTxt.push(this.translate.instant('ROLE.WORK_ASSIGNMENT.EDIT_AND_DUP_WA'));
        if (key == 'workAssignment_SeeRegistrations')
          permissionTxt.push(this.translate.instant('ROLE.WORK_ASSIGNMENT.SEE_REGISTRATIONS'));
        if (key == 'workAssignment_ViewAllWorkAssignment')
          permissionTxt.push(this.translate.instant('ROLE.WORK_ASSIGNMENT.VIEW_ALL_WA'));
        if (key == 'exposureForm_Add') permissionTxt.push(this.translate.instant('ROLE.EXPOSRE_FORM.REGISTER'));
        if (key == 'taskManager_ViewAll')
          permissionTxt.push(this.translate.instant('ROLE.TASK_MANAGER.VIEW_ALL_RISK_MEASURE_TASKS'));
        if (key == 'taskManager_EditAllRiskTask')
          permissionTxt.push(this.translate.instant('ROLE.TASK_MANAGER.RISK_MEASURE_TASK_MANAGER'));
        if (key == 'taskManager_AccessSubstitutionModule')
          permissionTxt.push(this.translate.instant('ROLE.TASK_MANAGER.ACCESS_SUBSTITUTION_MODULE'));
        if (key == 'taskManager_ViewAllSubstitutionTask')
          permissionTxt.push(this.translate.instant('ROLE.TASK_MANAGER.VIEW_ALL_SUBSTITUTION_TASK'));
        if (key == 'taskManager_SubstitutionManager')
          permissionTxt.push(this.translate.instant('ROLE.TASK_MANAGER.SUBSTITUTION_MANAGER'));
        if (key == 'project_View') permissionTxt.push(this.translate.instant('ROLE.PROJECT.TOOLTIP_VIEW_PROJECT'));
        if (key == 'project_Manage') permissionTxt.push(this.translate.instant('ROLE.PROJECT.TOOLTIP_MANAGE_PROJECT'));
        if (key == 'hub_View') permissionTxt.push(this.translate.instant('ROLE.HUB_SHARING.TOOLTIP_VIEW_HUB'));
        if (key == 'hub_Manage') permissionTxt.push(this.translate.instant('ROLE.HUB_SHARING.TOOLTIP_MANAGE_HUB'));
        if (key == 'others_Print') permissionTxt.push(this.translate.instant('ROLE.OTHERS.PRINT'));
        if (key == 'others_ReadAllDepartments')
          permissionTxt.push(this.translate.instant('ROLE.OTHERS.READ_ALL_DEPARTMENTS'));
        if (key == 'others_Administrator') permissionTxt.push(this.translate.instant('SIDE_BAR.ADMINISTRATOR'));
        if (key == 'others_SDSCreator') permissionTxt.push(this.translate.instant('SIDE_BAR.PUBLISHER'));
        if (key == 'report_Exposure') permissionTxt.push(this.translate.instant('COMMON.EXPOSURE_REPORT'));
        if (key == 'report_RiskAssessment')
          permissionTxt.push(this.translate.instant('SIDE_BAR.RISK_ASSESSMENT_REPORT'));
        if (key == 'report_Stock') permissionTxt.push(this.translate.instant('REPORT.STOCK_REPORT'));
        if (key == 'report_Substitution') permissionTxt.push(this.translate.instant('REPORT.SUBSTITUTION_REPORT'));
        if (key == 'report_ShowIDAndWorkTitle')
          permissionTxt.push(this.translate.instant('ROLE.REPORT.SHOW_ID_AND_WORK_TITLE'));
        if (key == 'userAccess_LocalEdit' || key == 'userAccess_FullEdit')
          permissionTxt.push(this.translate.instant('SIDE_BAR.USER_ACCESS_MANAGER'));
      }
      const log: LogModel = new LogModel();
      log.logType = LogType.changePermissionOnRole;
      log.description = ` updated role  <strong>${
        this.generalForm.value.name
      }</strong> permission from <strong>${permissionTxt.join(', ')}</strong>`;
      log.descriptionNo = ` oppdatert rolle <strong>${
        this.generalForm.value.name
      }</strong> tillatelse fra <strong>${permissionTxt.join(', ')}</strong>`;
      this.logService.saveLog(log).subscribe();
    }
  }

  openSelectLocationDialog() {}

  openModifyUserRolesPopup() {}

  // async getSecurityGroupsForAssetDialog(request: SearchRoleFilter): Promise<QMSAccessPagingModel<QMSAccessItem>> {
  //   const userGroupPaging = await this.roleService.getSecurityGroupsPaging(request).toPromise();
  //   const result: QMSAccessPagingModel<QMSAccessItem> = {
  //     data: userGroupPaging.chemicalSecGroup.map((item) => ({ id: item.id, name: item.name } as QMSAccessItem)),
  //     length: userGroupPaging.totalCount,
  //     pageIndex: 0,
  //     pageSize: 10,
  //   };
  //   return result;
  // }
}
